/**
 * About page CMS copy.
 *
 * Source of truth: `/app/memory/PooPluckers_About_Copy.md`.
 *
 * Hard rules baked into this file:
 *   - Brand caps: `Pluck`, `Pluckers`, `Plucking`, `PooPartner`,
 *     `PooPluckers` are ALWAYS capitalised. A pre-commit grep
 *     (`[a-z]pluck`, `[a-z]lucker`) must return zero hits here.
 *   - Honesty constraint: NO background-check / insurance claims.
 *     `Trust & Safety` items only describe what is actually live or
 *     softly "coming soon" — never aspirational certification.
 *   - All copy strings live here. The JSX (`pages/About.jsx`) is a
 *     pure layout layer that consumes named exports.
 */
export const ABOUT_HERO = {
  eyebrow: "Our story",
  // Feb 2026 launch-blocking Item 14 refinement (phrase-accent, not
  // full-line). Previously this hero rendered as
  //   "Spotless yards. Happy dogs.  Local pros who love both."
  // with the ENTIRE second sentence painted orange via a legacy
  // `<span class="acc">{headline_accent}</span>` full-line wrap. The
  // refined rule caps each headline at ONE phrase accent, so:
  //   - `headline_primary` carries the full first-line copy.
  //   - `headline_accent`  is now a PHRASE (default: "Happy dogs")
  //     wrapped in the shared brand token via the shipped
  //     `<AccentHeadline>` helper — same mechanism every section H2
  //     already uses on this page.
  //   - `headline_suffix`  is the second-line copy, rendered in
  //     plain slate (no color) so the accent stays visually singular.
  // Downstream: any consumer that still reads `headline_accent` will
  // see a shorter string; the `<AccentHeadline>` helper is a strict
  // substring match, so the shortened accent still highlights inside
  // `headline_primary` and the render is stable.
  headline_primary: "Spotless yards. Happy dogs.",
  headline_accent: "Happy dogs",
  headline_suffix: "Local pros who love both.",
  intro:
    "PooPluckers takes the one chore no dog owner enjoys off your plate. We connect local dog owners with reliable, local PooPartners who keep your yard clean \u2014 on your schedule. Spend less time scooping and more time enjoying your dog and your yard. Now recruiting PooPartners and launching neighborhood by neighborhood. \ud83d\udc3e",
  image_src: "/landing-frisbee",
  image_alt:
    "A happy dog mid-leap catching a frisbee in a sunny backyard \u2014 the kind of yard a PooPartner keeps spotless.",
};

export const ABOUT_MARKETPLACE = {
  kick: "How it works",
  headline: "A marketplace built for both sides.",
  // Item 14 (Feb 2026 launch-blocking batch) — orange accent phrase
  // that the section H2 wraps via `<AccentHeadline>`. Substring MUST
  // appear verbatim in `headline`. See `lib/AccentHeadline.jsx`.
  headline_accent: "both sides",
  body:
    "PooPluckers is two-sided by design. On one side are dog owners who want a clean yard without the chore. On the other are PooPartners \u2014 established local service businesses that add PooPluckers as an additional demand channel. We bring the two together, handle marketing, booking, and secure payments, and stay out of the way so the work gets done.",
  customer_card: {
    title: "For dog owners",
    bullets: [
      "Join the waitlist and be first in line when booking opens in your area.",
      "Transparent per-dog pricing \u2014 first-dog base + a small add-on for each extra pup.",
      "Matched with a PooPartner who serves your area.",
      "Pay through Stripe. Funds are paid out after the Plucking is complete.",
    ],
    cta_label: "Join the waitlist",
    cta_to: "/#waitlist",
  },
  partner_card: {
    title: "For PooPartners",
    bullets: [
      "Pick the ZIPs your crew already covers.",
      "Choose the days and hours that fit your existing route.",
      "Get matched with customers in your service area \u2014 we bring them to you.",
      "Stripe Connect payouts direct to your bank, on Stripe\u2019s standard payout schedule.",
    ],
    cta_label: "Become a PooPartner",
    cta_to: "/signup?role=provider",
  },
  // Feb 2026 amendment (see PRD §Pricing Config Change Protocol):
  // the previous "We keep the cut small." section exposed the
  // commission ladder (85–94% partner keep implied a 6–15% platform
  // cut) AND made a "recurring discount built in" claim the booking
  // engine does not currently apply. Both were retired in favor of
  // rate-free copy that stays honest: partners keep more than going
  // solo (true — the fee schedule shown at signup is more favorable
  // than most cleaner platforms), full breakdown available at
  // signup (true — the signup flow surfaces the tier ladder). No
  // percentage is ever named on public marketing.
  cut_headline: "Partners keep more than going solo.",
  // Item 14 — accent phrase for the `cut_headline` H2.
  cut_headline_accent: "keep more",
  cut_body:
    "Local businesses do the work; we handle the platform, the booking, and Stripe-secured payouts. Full fee schedule is shown at signup.",
  cut_note_prefix: "See the tier options on the ",
  cut_note_link_label: "pricing page",
  cut_note_link_to: "/pricing",
  cut_note_suffix: ".",
  cut_image_src: "/landing-frisbee",
  cut_image_alt:
    "A golden retriever leaping for a frisbee in a freshly tidied backyard — the kind of clean PooPartners deliver to neighborhood dog owners.",
};

export const ABOUT_TRUST = {
  kick: "Trust & safety",
  headline: "Stripe-secured payments. Honest reviews.",
  // Item 14 — accent phrase for the Trust & safety H2.
  headline_accent: "Honest reviews",
  body:
    "We\u2019re building the parts a marketplace needs to be safe to use — and we\u2019re shipping them in the right order, not all at once.",
  // CMS rule: NEVER include a background-check item here. Every entry
  // below maps to functionality that is either live now or genuinely
  // queued — no aspirational claims.
  items: [
    {
      key: "stripe-identity",
      icon: "ShieldCheck",
      title: "Stripe identity verification",
      body:
        "PooPartners will complete Stripe identity verification before they can accept any Plucking. Same standard countless marketplaces rely on \u2014 rolling out with our first neighborhoods.",
      note: "At launch",
    },
    {
      key: "stripe-escrow",
      icon: "Lock",
      title: "Stripe payouts",
      body:
        "Payments are handled by Stripe \u2014 funds are paid out after a successful Plucking, and PooPartners cash out through Stripe Connect.",
      note: "At launch",
    },
    {
      key: "honest-reviews",
      icon: "Star",
      title: "Honest customer reviews",
      body:
        "Once we open service to a neighborhood, reviews flow back to PooPartner profiles. No fake testimonials, no paid placement.",
      note: "Launching with our first neighborhoods",
    },
  ],
};

export const ABOUT_STORY = {
  kick: "The \u201CPluck for Poo\u201D story",
  headline: "We built this because the alternative was worse.",
  // Item 14 — accent phrase for the story H2. The contrastive noun
  // ("the alternative") carries the emotional weight of the sentence.
  headline_accent: "the alternative",
  paragraphs: [
    "Most dog owners we know fall into one of two camps. They either accept their yard as a slow-growing minefield, or they shell out for a subscription that costs more than streaming and locks them in for a year. Neither felt right.",
    "On the other side, established local service businesses \u2014 dog-waste removal operators, lawn and landscaping crews, and small pet-service outfits \u2014 are perfectly able to Pluck a yard well. They just need a way to be found, booked, and paid.",
    "So we put both sides in the same place. Dog owners get a lean, transparent way to book. PooPartners get a steady stream of work routed to their existing service area. We handle the platform work \u2014 booking, matching, secure payments \u2014 so PooPartners can focus on the yard. That\u2019s the deal.",
  ],
  pullquote:
    "\u201CWe\u2019re betting local-first wins. Big-platform extraction loses.\u201D",
  pullquote_attrib: "\u2014 The PooPluckers team",
};

/**
 * Longer "Our story" overview block — slots in between the Trust &
 * Safety band and the "Pluck for Poo" origin narrative as a clean
 * mid-page intro. Three paragraphs verbatim from the
 * brand-supplied copy spec; trailing paw emoji preserved on the
 * closing paragraph. Uses the same kick + paragraphs shape as
 * `ABOUT_STORY` so the JSX consumer reuses existing typography.
 *
 * Heading note: the hero section ALREADY uses "Our story" as its
 * eyebrow (see `ABOUT_HERO.eyebrow`). To avoid two identical
 * section headings stacking down the page, this mid-page block
 * uses "Why we built this" — narratively complementary to the
 * Pluck-for-Poo origin story that follows immediately below it.
 */
export const ABOUT_OUR_STORY = {
  kick: "Why we built this",
  paragraphs: [
    "PooPluckers started with a simple idea: nobody should dread their own backyard. We\u2019re building a friendly, local marketplace that connects local dog owners with PooPartners \u2014 established local service businesses that handle yard cleanup so you don\u2019t have to.",
    "For dog owners: join the waitlist and we\u2019ll email you the moment PooPluckers is live in your ZIP. Set your schedule then, and enjoy a spotless yard and a happy dog. For PooPartners: PooPluckers routes booked customers to the ZIPs you already cover \u2014 we handle marketing, matching, and billing while you handle the yard.",
    "We\u2019re launching neighborhood by neighborhood, expanding metro by metro. Want clean yards in your area, or want to add PooPluckers to your local service business? Join us. \ud83d\udc3e",
  ],
};

export const ABOUT_CTA = {
  headline: "Ready to make your yard a little fresher?",
  sub:
    "We're launching neighborhood by neighborhood \u2014 join the waitlist " +
    "to be first in line when booking opens in your area, or join the " +
    "network and start earning where you already work.",
  customer_label: "Join the waitlist",
  customer_to: "/#waitlist",
  partner_label: "Become a PooPartner",
  partner_to: "/signup?role=provider",
};
